// [PDF-EXPORT 2026-09-30, user] NEW FILE — one-click "Download PDF" on the EOM tab (EOM view only).
// A4 portrait, tables only, no chart: the EOM table, Department Wise Revenue, the Next Month
// Provisional Inventory line, and PM Retention Status as one row per 10-day window.
//
// Nothing is calculated here. EOMPage passes every cell already formatted, built from the SAME
// variables and formatters its own tables render, so the file cannot say anything the screen does
// not. This module only lays the cells out, following pmPerformanceReport.ts (jspdf + autoTable,
// blue header row, blue-grey Total row, doc.save).
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { CellHookData, UserOptions } from 'jspdf-autotable';
import { fmtMonthYear } from './dates';

/** One table row: the label, then the other cells exactly as the page shows them. */
export interface EomPdfRow {
  label: string;
  cells: string[];
  /** The page draws this row bold (its Gross / Net lines). */
  bold?: boolean;
}

export interface EomPdfInput {
  /** 'YYYY-MM': the month the EOM view is showing. */
  month: string;
  /** The EOM table's rows in page order, then its closing Net SEO Total row. */
  eomRows: EomPdfRow[];
  eomTotal: EomPdfRow;
  /** Department Wise Revenue: one row per team, then its Total row. */
  deptRows: EomPdfRow[];
  deptTotal: EomPdfRow;
  /** Next Month Provisional Inventory, formatted. */
  provisional: string;
  /** One row per period. label = the period ("Sep 1–10, 2026" … "Overall Sep 2026"),
   *  cells = [Retention Expected, Retention Achieved, Achieved %]. */
  retention: EomPdfRow[];
}

const EOM_HEAD = ['Particulars', 'Ranking', 'Creative', 'Tech', 'Performance', 'Total'];
const DEPT_HEAD = ['Team', 'Gross', 'Refund/Dispute', 'Dispute Won/Recovery', 'Net'];
const RETENTION_HEAD = ['Period', 'Retention Expected', 'Retention Achieved', 'Achieved %'];

type RGB = [number, number, number];
const MARGIN = { top: 42, right: 40, bottom: 40, left: 40 };
const TITLE_GAP = 10; // from a section title's baseline to the top of its table
const HEAD_FILL: RGB = [68, 114, 196];
const FOOT_FILL: RGB = [222, 230, 241];
const BOLD_FILL: RGB = [242, 242, 242];

const finalY = (doc: jsPDF) => (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

function tableOptions(head: string[], rows: EomPdfRow[], totals: EomPdfRow[]): UserOptions {
  const boldRows = new Set(rows.flatMap((r, i) => (r.bold ? [i] : [])));
  return {
    head: [head],
    body: rows.map((r) => [r.label, ...r.cells]),
    foot: totals.map((r) => [r.label, ...r.cells]),
    // [PDF-KEEP-TOGETHER] If a table ever outgrows a whole page, its Total prints once, at the end.
    showFoot: 'lastPage',
    theme: 'grid',
    rowPageBreak: 'avoid',
    styles: {
      font: 'helvetica', fontSize: 9, cellPadding: { top: 3.5, right: 5, bottom: 3.5, left: 5 },
      lineColor: [200, 200, 200], lineWidth: 0.5, textColor: [0, 0, 0],
    },
    headStyles: { fillColor: HEAD_FILL, textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: FOOT_FILL, textColor: [0, 0, 0], fontStyle: 'bold' },
    margin: MARGIN,
    /* [PDF-TOTAL-ALIGN] autoTable applies columnStyles to BODY rows only, which is how the PM
       Performance PDF once printed its Total row left-aligned under right-aligned figures. So the
       alignment is set on EVERY cell here: the label column left, every figure right, in the
       header, the body and the Total row alike. Each column keeps one right edge. */
    didParseCell: (data: CellHookData) => {
      data.cell.styles.halign = data.column.index === 0 ? 'left' : 'right';
      if (data.section === 'body' && boldRows.has(data.row.index)) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = BOLD_FILL;
      }
    },
  };
}

/** [PDF-KEEP-TOGETHER] How tall a table will be, found by drawing it once on a scratch A4 page.
 *  Exact, where a per-row estimate is not (a wrapped header, a bold row). Infinity = it cannot fit
 *  on one page at all. */
function tableHeight(options: UserOptions): number {
  const scratch = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  autoTable(scratch, { ...options, startY: MARGIN.top });
  return scratch.getNumberOfPages() > 1 ? Infinity : finalY(scratch) - MARGIN.top;
}

/** [PDF-KEEP-TOGETHER] A centred title and its table, always on the same page: when the table would
 *  not fit under the title, both start on a new page. Returns the y just under the table. */
function drawSection(doc: jsPDF, y: number, title: string, options: UserOptions): number {
  if (y > MARGIN.top && y + TITLE_GAP + tableHeight(options) > doc.internal.pageSize.getHeight() - MARGIN.bottom) {
    doc.addPage();
    y = MARGIN.top;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.text(title, doc.internal.pageSize.getWidth() / 2, y, { align: 'center' });
  autoTable(doc, { ...options, startY: y + TITLE_GAP });
  return finalY(doc);
}

/** Build the EOM PDF and download it straight away as "EOM <Mon YYYY>.pdf". */
export function downloadEomReport(input: EomPdfInput): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const monthLabel = fmtMonthYear(input.month);
  // [PDF-EXPORT 2026-09-30, user] "heading should use the complete month name": the two headings say
  // "September 2026", like the EOM tab's own "EOM | September 2026". The file name keeps the short form.
  const ymMatch = /^(\d{4})-(\d{2})/.exec(input.month);
  const headingMonth = ymMatch
    ? new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(Number(ymMatch[1]), Number(ymMatch[2]) - 1))
    : monthLabel;
  let y = MARGIN.top;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(20, 20, 20);
  doc.text(`EOM · ${headingMonth}`, pageW / 2, y, { align: 'center' });
  // [PDF-EXPORT 2026-09-30, user] The "Downloaded <date>" line under the title was removed on request.
  y += 30;

  y = drawSection(doc, y, `EOM | ${headingMonth}`, tableOptions(EOM_HEAD, input.eomRows, [input.eomTotal])) + 26;
  y = drawSection(doc, y, 'Department Wise Revenue', tableOptions(DEPT_HEAD, input.deptRows, [input.deptTotal])) + 24;

  if (y > pageH - MARGIN.bottom) { doc.addPage(); y = MARGIN.top; }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.text(`Next Month Provisional Inventory – ${input.provisional}`, pageW / 2, y, { align: 'center' });
  y += 30;

  // The page shows "Overall <month>" as a fourth period beside the three 10-day windows. It covers
  // the whole month, so here it is the table's Total row.
  const isOverall = (r: EomPdfRow) => /^overall\b/i.test(r.label.trim());
  drawSection(doc, y, 'PM Retention Status – 10-Day Breakdown', tableOptions(
    RETENTION_HEAD, input.retention.filter((r) => !isOverall(r)), input.retention.filter(isOverall),
  ));

  doc.save(`EOM ${monthLabel}.pdf`);
}
